export type AudioSettingsSnapshot = {
  masterVolume: number;
  musicEnabled: boolean;
};

type Listener = () => void;

/** Default master volume on first load. Matches `mutedVolumeMemory` default
 *  so the user's first unmute (without ever setting volume manually) lands
 *  here too, instead of jumping to full. */
const DEFAULT_MASTER_VOLUME = 0.7;

export class AudioSettingsStore {
  private masterVolume = DEFAULT_MASTER_VOLUME;
  private musicEnabled = true;
  // Volume restored on unmute. Updated ONLY when `mute()` is called
  // explicitly — slider drag-to-zero does NOT update memory, so a user
  // who drags down then clicks unmute gets a sensible jump back rather
  // than a restoration to the last sub-zero slider stop.
  private mutedVolumeMemory = DEFAULT_MASTER_VOLUME;
  private readonly listeners = new Set<Listener>();

  public getSnapshot(): AudioSettingsSnapshot {
    return {
      masterVolume: this.masterVolume,
      musicEnabled: this.musicEnabled,
    };
  }

  /** Direct volume write. Used by the slider drag path. Does NOT update mute memory. */
  public setMasterVolume(volume: number): void {
    const clamped = Math.max(0, Math.min(1, volume));
    if (this.masterVolume === clamped) return;
    this.masterVolume = clamped;
    this.emit();
  }

  /** Save current volume to memory and silence. No-op if already muted. */
  public mute(): void {
    if (this.masterVolume === 0) return;
    this.mutedVolumeMemory = this.masterVolume;
    this.setMasterVolume(0);
  }

  /** Restore the volume saved by the last `mute()` call. No-op if not muted. */
  public unmute(): void {
    if (this.masterVolume > 0) return;
    const restoreTo = this.mutedVolumeMemory > 0 ? this.mutedVolumeMemory : DEFAULT_MASTER_VOLUME;
    this.setMasterVolume(restoreTo);
  }

  public toggleMute(): void {
    if (this.masterVolume > 0) this.mute();
    else this.unmute();
  }

  public setMusicEnabled(enabled: boolean): void {
    if (this.musicEnabled === enabled) return;
    this.musicEnabled = enabled;
    this.emit();
  }

  public subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
